import { useCallback, useState, useContext } from 'react';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useSelector } from 'react-redux';
import { ActionIcon, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { UsersApiResponse } from '../../../type/entities/user-type';
import { SpecieService } from '../../../services/specie/specieService';
import { SpeciesModal } from './components/SpeciesModal';
import SpeciesFilters from './SpeciesFilters';
import { ImportSpeciesModal } from './components/ImportSpeciesModal';
import { menuRoutes } from '../../../router/menu';
import { HiOutlineEye } from 'react-icons/hi2';
import { Link } from 'react-router-dom';  
import { speciesGroupSelector } from '../../../utils/SelectorMapping';
import { PrivilegeContext } from '../../../components/priviledge/PriviledgeProvider';

interface SpeciesListProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    isOpenImport: boolean;
    setIsOpenImport: (isOpen: boolean) => void;
}

const columnOrder = [
    'id',
    'commonName',
    'scientificName',
    'family',
    'specieIdentifier',
    'acronym',
    'censusAvailable',
    'ofcAvailable',
    'issueAvailable',
    'carrionAvailable',
    'catAndalucia',
    'riskLevelAndalucia',
    'catNacional',
    'riskLevelNacional',
    'infoPage'
];

const SpeciesList: React.FC<SpeciesListProps> = ({ isOpen, setIsOpen, isOpenImport, setIsOpenImport }) => {

    // STATES

    const [specie, setSpecie] = useState(null);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const specieService = new SpecieService();

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const { userCan } = useContext(PrivilegeContext);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE ESPECIES
     * @EN GETS THE LIST OF SPECIES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error,refetch] = useFetch(useCallback(async () => {
        const response = await specieService.listSpecies(filters);
        return response.getResponseData() as UsersApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------
   
    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACIÓN DE UNA ESPECIE
     * @EN HANDLES THE DELETION OF A SPECIES
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await specieService.deleteSpecie(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Especie eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIAS ESPECIES
     * @EN HANDLES THE DELETION OF MULTIPLE SPECIES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await specieService.deleteMultiSpecie(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuarios eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL CERRADO DEL MODAL
     * @EN HANDLES THE CLOSING OF THE MODAL
     */
    // -------------------------------------------------------------------------------------------------------------
    const onCloseModal = () => {
        setIsOpen(false);
        setIsOpenImport(false);
        setTimeout(() => {
            setSpecie(null);
        }, 150);
    }
    // -------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page>
            {data !== undefined && (
                <CustomTable
                    divClassName='max-h-table'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    id={'species-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    overrideColumns={[
                            {
                                key: 'commonName',
                                render: (row: any) => {
                                    if (userCan('get', 'species')) {
                                        return (
                                            <a href={`${menuRoutes.species.path}/${row.row.original.id}/profile/${menuRoutes.species.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                                {row.row.original.commonName}
                                            </a>
                                        );
                                    } else {
                                        return <span>{row.row.original.commonName}</span>;
                                    }
                                }
                            },
                            {
                                key: 'family',
                                render: (row: any) => {
                                    return (
                                        <span>
                                            {speciesGroupSelector.find((group) => group.value === row.row.original.family)?.label || '-'}
                                        </span>
                                    );
                                }
                            },
                            {
                                    key: 'scientificName',
                                    render: (row: any) => {
                                        return (
                                            <Text className="text-gray-700 italic">
                                                {row.row.original.scientificName || 'No disponible'}
                                            </Text>
                                        );
                                    }
                            },
                            {
                                key: 'censusAvailable',
                                render: (row: any) => {
                                    return row.row.original.censusAvailable ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'ofcAvailable',
                                render: (row: any) => {
                                    return row.row.original.ofcAvailable ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'issueAvailable',
                                render: (row: any) => {
                                    return row.row.original.issueAvailable ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'carrionAvailable',
                                render: (row: any) => {
                                    return row.row.original.carrionAvailable ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'catAndalucia',
                                render: (row: any) => {
                                    return row.row.original.catAndalucia ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'catNacional',
                                render: (row: any) => {
                                    return row.row.original.catNacional ? (
                                        <span>Sí</span>
                                    ) : (
                                        <span>No</span>
                                    );
                                }
                            },
                            {
                                key: 'riskLevelAndalucia',
                                render: (row: any) => {
                                    const { catAndalucia, riskLevelAndalucia } = row.row.original;
                                    const level = riskLevelAndalucia === 'null' ? null : riskLevelAndalucia;
                                    if (!catAndalucia || !level) {
                                        return <span className="text-gray-500">N/A</span>;
                                    }
                                    return <span>{level}</span>;
                                }
                            },
                            {
                                key: 'riskLevelNacional',
                                render: (row: any) => {
                                    const { catNacional, riskLevelNacional } = row.row.original;
                                    const level = riskLevelNacional === 'null' ? null : riskLevelNacional;
                                    if (!catNacional || !level) {
                                        return <span className="text-gray-500">N/A</span>;
                                    }
                                    return <span>{level}</span>;
                                }
                            },
                            {
                                key: 'infoPage',
                                render: (row: any) => {
                                    return (
                                        <>{
                                        row.row.original.infoPage == null || row.row.original.infoPage === '' ? (
                                                <span>No disponible</span>
                                            ) : (
                                            <a href={row.row.original.infoPage || '#'} target="_blank" rel="noreferrer" className='text-primary hover:text-primary hover:underline'>
                                                {row.row.original.infoPage || 'No disponible'}
                                            </a>
                                            )}
                                        </>
                                    )
                                    
                                }
                            }
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'species', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver especie'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.species.path}/${row.id}/profile/${menuRoutes.species.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-5 h-5"
                                            >
                                                <HiOutlineEye className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'user', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar especie'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setIsOpen(true)
                                                setSpecie(row);
                                            }}
                                        >
                                            <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },  
                        {
                            label: 'Delete',
                            permissions: { group: 'user', permission: 'delete' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar especie'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                actionIconClassName='w-5 h-5'
                                                title={`Eliminar especie`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.commonName}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    handleMultipleDelete={handleMultiDelete}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as UsersApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Especies'}>
                <SpeciesFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <SpeciesModal
                isOpen={isOpen}
                onClose={onCloseModal}
                refetch={refetch}
                specie={specie}
            />

            <ImportSpeciesModal
                isOpen={isOpenImport}
                onClose={onCloseModal}
                refetch={refetch}
            />

        </Page>
    );
};

export default SpeciesList;